import { __rest } from 'tslib';
import React, { useRef, useState, useMemo, useEffect } from 'react';
import { InjectorService } from '@tencent/ioc';
import { Transition } from 'react-transition-group';
import { TransitionService } from '../../services/transition/transition-service.js';
import { useObservableState } from '../toolbar/use-observable-state.js';
import { StateManager } from '../../services/store/state-manager.js';

/**
 * 动画组件，在 Transition 基础上包装以获取配置化的动画属性和状态属性
 *
 * @alpha
 */
function TransitionWrapper(props) {
    const { name, children, simulateSpaceChange, className = '', syncWithTarget = true } = props, originalProps = __rest(props, ["name", "children", "simulateSpaceChange", "className", "syncWithTarget"]);
    const store = InjectorService.get(StateManager);
    const transitionStyle = InjectorService.get(TransitionService).get(name);
    const ref = useRef(null);
    const [targetWidth, setTargetWidth] = useState(0);
    const [targetHeight, setTargetHeight] = useState(0);
    const observer = useMemo(() => new ResizeObserver(() => {
        var _a, _b, _c;
        const { width, height } = (_c = (_b = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.previousElementSibling) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect()) !== null && _c !== void 0 ? _c : {};
        if (width && width !== targetWidth) {
            setTargetWidth(width);
        }
        if (height && height !== targetHeight) {
            setTargetHeight(height);
        }
    }), [targetHeight, targetWidth]);
    useEffect(() => {
        var _a;
        if (!((_a = ref.current) === null || _a === void 0 ? void 0 : _a.previousElementSibling) || !simulateSpaceChange) {
            return;
        }
        observer.observe(ref.current.previousElementSibling);
        return () => {
            observer.disconnect();
        };
    }, [simulateSpaceChange, observer]);
    const storeKey = `transition.trigger.${name}`;
    const trigger = useObservableState(storeKey, true);
    const domVisibleKey = `${name}.dom.visible`;
    const syncStateOnAnimationEnd = () => {
        store.upsert(domVisibleKey, trigger);
    };
    return (React.createElement(Transition, Object.assign({}, originalProps, { timeout: transitionStyle.duration, in: trigger, onEnter: syncStateOnAnimationEnd, onExit: syncStateOnAnimationEnd }), (state) => {
        var _a;
        const stateStyle = transitionStyle[state];
        const { height, width } = stateStyle, rest = __rest(stateStyle, ["height", "width"]);
        const { transition } = stateStyle;
        const modifiedStyle = Object.assign(Object.assign({}, children.props.style), rest);
        if (simulateSpaceChange) {
            modifiedStyle.position = 'absolute';
        }
        return React.createElement(React.Fragment, null,
            React.cloneElement(children, { style: modifiedStyle, key: storeKey }),
            React.createElement("div", { id: "transition-space", className: [className, (_a = children.props.className) !== null && _a !== void 0 ? _a : ''].join(' '), ref: ref, style: {
                    opacity: 0,
                    position: simulateSpaceChange ? 'relative' : 'absolute',
                    transition,
                    border: 'none',
                    margin: 0,
                    padding: 0,
                    pointerEvents: 'none',
                    height: syncWithTarget ? (height !== null && height !== void 0 ? height : targetHeight) : height,
                    width: syncWithTarget ? (width !== null && width !== void 0 ? width : targetWidth) : width,
                } }));
    }));
}

export { TransitionWrapper };
//# sourceMappingURL=transition.js.map
